import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

type NavItem = {
  label: string;
  to: string;
  section?: string; // Section identifier for RBAC
  ownerOnly?: boolean; // Only visible to studio owners
};

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', section: 'dashboard' },
  { label: 'Projects', to: '/projects', section: 'projects' },
  { label: 'Payments', to: '/payments', section: 'payments' },
  { label: 'Staff', to: '/staff', section: 'staff' },
  { label: 'Expenses', to: '/expenses', section: 'expenses' },
  { label: 'Management', to: '/management', ownerOnly: true },
];

const StudioNavBar: React.FC = () => {
  const { user, logout, canAccess, isStudioOwner, isStaff } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
  };

  // Filter nav items based on user role and permissions
  const filteredNavItems = navItems.filter((item) => {
    if (item.ownerOnly) {
      return isStudioOwner;
    }
    if (item.section) {
      return canAccess(item.section);
    }
    return true;
  });

  // Get display name for user badge
  const getDisplayName = () => {
    if (isStaff && user) {
      const firstName = user.first_name || '';
      const lastName = user.last_name || '';
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim();
      }
    }
    return user?.studio_name ?? user?.username;
  };

  return (
    <nav className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        {/* Logo Section */}
        <div className="flex items-center gap-3">
          <img 
            src="/SOMS_logo.png" 
            alt="SOMS Logo" 
            className="h-10 w-10 rounded-xl object-contain shadow-md" 
          />
          <div className="hidden sm:block">
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">SOMS Studio</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {isStaff ? 'Staff Workspace' : 'Operations Dashboard'}
            </p>
          </div>
          <div className="block sm:hidden">
            <h1 className="text-base font-bold text-slate-900 dark:text-white">SOMS</h1>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-3 text-sm font-medium text-slate-600 md:flex dark:text-slate-200">
          {filteredNavItems.map((item) => {
            const itemPath = item.to.split('?')[0];
            const itemSearch = item.to.includes('?') ? item.to.split('?')[1] : '';
            const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
            const isActive = location.pathname === itemPath && (!itemSearch || currentSearch === itemSearch);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-full px-3 py-1 transition ${
                  isActive
                    ? 'nav-link-active'
                    : 'nav-link-inactive'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>

        {/* Desktop User & Logout */}
        <div className="hidden items-center gap-4 md:flex">
          <div className="items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hidden sm:flex dark:bg-slate-800 dark:text-slate-200">
            <span className={`h-2 w-2 rounded-full ${isStaff ? 'bg-purple-500' : 'bg-emerald-500'}`} />
            <span className="truncate max-w-xs">{getDisplayName()}</span>
            {isStaff && <span className="text-purple-600 dark:text-purple-400">(Staff)</span>}
          </div>
          <button
            onClick={logout}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 whitespace-nowrap"
          >
            Logout
          </button>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 transition dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="border-t border-slate-200 bg-white md:hidden dark:border-slate-800 dark:bg-slate-900">
          <div className="space-y-1 px-4 py-3">
            {filteredNavItems.map((item) => {
              const itemPath = item.to.split('?')[0];
              const itemSearch = item.to.includes('?') ? item.to.split('?')[1] : '';
              const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
              const isActive = location.pathname === itemPath && (!itemSearch || currentSearch === itemSearch);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block rounded-lg px-4 py-2 text-sm font-medium transition ${
                    isActive
                      ? 'nav-link-active-mobile'
                      : 'nav-link-inactive-mobile'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="border-t border-slate-200 pt-3 mt-3 dark:border-slate-800">
              <div className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-50 rounded-lg mb-2 flex items-center gap-2 dark:bg-slate-800 dark:text-slate-200">
                <span className={`h-2 w-2 rounded-full ${isStaff ? 'bg-purple-500' : 'bg-emerald-500'}`} />
                <span className="truncate">{getDisplayName()}</span>
                {isStaff && <span className="text-purple-600 dark:text-purple-400">(Staff)</span>}
              </div>
              <button
                onClick={handleLogout}
                className="w-full rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default StudioNavBar;




